import { graphql, usePreloadedQuery } from 'react-relay';
import { useSuspenseResource } from '@atlassian/react-resource-router/src/resources/controllers/use-resource/suspense.tsx';
import { appChromeResource } from './appChromeResource.tsx';

import type { useJiraAppChromeQuery as useJiraAppChromeQueryType } from './__generated__/useJiraAppChromeQuery.graphql.ts';

/**
 * The data returned by {@link useJiraAppChromeQuery}: the raw `Query`-level
 * result that spreads both `...AppChrome` (the GlobalRoot contract) and
 * `...TopNavigation_slotsQuery` (Jira's top-nav data). GlobalRoot forwards this as the
 * `TopNavSlot` `queryRef`, so the slot can `useFragment(TopNavigation_slotsQuery, …)`
 * with no cast.
 *
 * The Relay-hook approach is necessary to solve a specific set of requirements:
 *
 * 1. Allow the platform component AND the host-app to connect to the same Relay query.
 *   An app’s navigation-chrome (top nav, sidebar, banner) needs Relay data.
 *   The platform component (GlobalRoot) needs specific data (call it dataset A)
 *   The app-itself needs its own specific data (called B)
 *   A & B may be very similar, but are likely to always diverge
 *   So we need a way to get A and B, when neither the Platform or the App know each other’s data requirements.
 *
 * 2. The app must own the query, as it might preload the data.
 *   There’s no way for a Platform component to drive this. Some apps will use useLazyLoadQuery.
 *   Others will use usePreloadedQuery. Platform must support either.
 *
 * 3. Apps must support the Platform data requirements (implement the ...AppChrome spread inside their query).
 *
 * 4. The GlobalRoot component must not suspend the main-content (islands of data principle).
 *   We are intentionally aiming to merge 2 existing islands - top nav and sidenav - but not in this PR.
 *   There should be performance benefits (in theory), but we will measure. If the benefits do not materialise,
 *   we can still support the status quo (as this PR demonstrates).
 *   To not-suspend above the main-content, we have an internal component called AppChrome, which is the one
 *   which calls the function passed to the useRelayQuery prop. AppChrome and “main content” are siblings in
 *   the component tree.
 */
export type useJiraAppChromeQueryData = useJiraAppChromeQueryType['response'];

export function useJiraAppChromeQuery(): useJiraAppChromeQueryData {
	const { data } = useSuspenseResource(appChromeResource);

	/* eslint-disable @atlassian/relay/no-wrapping-hooks, @atlassian/relay/must-colocate-fragment-spreads */
	const preloadedData = usePreloadedQuery<useJiraAppChromeQueryType>(
		graphql`
			query useJiraAppChromeQuery(
				$navPlatSidebarDataEntityAri: ID!
				$ownerAri: ID
				$skipNavPlatSidebarData: Boolean!
				$atlassianAccountId: ID!
				$cloudId: ID!
				$isAnonymous: Boolean!
				$issueKey: String!
				$shouldCheckProjectTypeForIssue: Boolean!
				$projectKey: String!
				$shouldCheckProjectType: Boolean!
				$projectIdOrKey: String!
				$shouldFetchProjectTypeForSettingsAndEA: Boolean!
				$shouldFetchProjectType: Boolean!
				$shouldFetchCSMLiveChatAgent: Boolean!
				$skipHelpButtonRelayRead: Boolean!
				$collabContextRoutingAri: String
			) {
				...AppChrome
				...TopNavigation_slotsQuery
			}
		`,
		data,
	);
	/* eslint-enable @atlassian/relay/no-wrapping-hooks, @atlassian/relay/must-colocate-fragment-spreads */

	return preloadedData;
}
